import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  fetchCompanyUsers,
  fetchPaginatedCompanyUsers,
  fetchUserRoleMetrics,
  createCompanyUser,
  updateCompanyUserStatus,
  updateCompanyUserRole,
  assignUserLoginCode,
  regenerateUserLoginCode,
} from '../services/userManagementService';
import {
  CreateCompanyUserRequest,
  UpdateCompanyUserRoleRequest,
  UpdateCompanyUserStatusRequest,
} from '../types/userManagement';

/**
 * Hook to query company users for a specific outlet
 */
export function useCompanyUsers(outletId?: string | null) {
  return useQuery({
    queryKey: queryKeys.users.list(outletId),
    queryFn: async () => {
      const res = await fetchCompanyUsers(outletId);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to fetch company users');
      }
      return res.data;
    },
  });
}

interface UseCompanyUsersPaginatedParams {
  outletId?: string | null;
  search?: string;
  page: number;
  pageSize: number;
}

/**
 * Paginated company users, returning PaginatedResponse<ManagedUser> shaped
 * for direct pass-through to <PaginationControls>, mirroring the Clients
 * page pagination pattern.
 */
export function useCompanyUsersPaginated(
  params: UseCompanyUsersPaginatedParams,
) {
  return useQuery({
    queryKey: queryKeys.users.paginatedList(params),
    queryFn: async () => {
      const res = await fetchPaginatedCompanyUsers(params);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to fetch company users');
      }
      return res.data;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to query user counts grouped by roles
 */
export function useUserRoleMetrics(outletId?: string | null) {
  return useQuery({
    queryKey: queryKeys.users.metrics(outletId),
    queryFn: async () => {
      const res = await fetchUserRoleMetrics(outletId);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to fetch user role metrics');
      }
      return res.data;
    },
  });
}

/**
 * Hook to execute user profile and passcode mutations
 */
export function useUserMutations() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (req: CreateCompanyUserRequest) => {
      const res = await createCompanyUser(req);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to create user');
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (req: UpdateCompanyUserStatusRequest) => {
      const res = await updateCompanyUserStatus(req);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to update user status');
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });

  const roleMutation = useMutation({
    mutationFn: async (req: UpdateCompanyUserRoleRequest) => {
      const res = await updateCompanyUserRole(req);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to update user role');
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });

  const passcodeMutation = useMutation({
    mutationFn: async ({
      userId,
      loginCode,
    }: {
      userId: string;
      loginCode: string | null;
    }) => {
      const res = await assignUserLoginCode(userId, loginCode);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to assign passcode');
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await regenerateUserLoginCode(userId);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to regenerate passcode');
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });

  return {
    createMutation,
    statusMutation,
    roleMutation,
    passcodeMutation,
    regenerateMutation,
  };
}
